import React, { useEffect, useRef, useState } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPopup,
} from "firebase/auth";
import { auth, sendVerificationEmail, useLoggedIn } from "./firebase";
import { FirebaseError } from "firebase/app";
import { Link, useNavigate } from "react-router-dom";
import { lstr, LocalizationStrings } from "./localization";
import { Pup } from "./Pup";

// localizedAuthErrorMessage maps the Firebase error codes a user can
// realistically trigger from this form to a localized, human message.
// Anything unexpected collapses to the generic message (the raw error is
// still logged for debugging).
function localizedAuthErrorMessage(
  err: unknown,
  strings: LocalizationStrings,
): string {
  console.error("Auth error:", err);
  if (!(err instanceof FirebaseError)) {
    return strings.login_error_generic;
  }
  switch (err.code) {
    case "auth/invalid-credential":
    case "auth/user-not-found":
    case "auth/wrong-password":
      return strings.login_error_invalid_credentials;
    case "auth/email-already-in-use":
      return strings.login_error_email_in_use;
    case "auth/weak-password":
      return strings.login_error_weak_password;
    case "auth/invalid-email":
      return strings.login_error_invalid_email;
    case "auth/too-many-requests":
      return strings.login_error_too_many_requests;
    default:
      return strings.login_error_generic;
  }
}

export function LoginPage({ isSignUp, l }: { isSignUp: boolean; l: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  // True while a sign-in request is in flight. It stays true on success: the
  // page navigates away once the login state flips.
  const [pending, setPending] = useState(false);
  // Where the verification email went after an email sign-up: the page then
  // says so instead of navigating away.
  const [verificationEmailSentTo, setVerificationEmailSentTo] = useState<
    string | null
  >(null);
  // Set when an email sign-up starts, so the redirect below doesn't leave as
  // soon as the account exists, before the verification email is sent.
  const emailSignUpStartedRef = useRef(false);
  const navigate = useNavigate();
  const strings = lstr(l);

  // Sign In with email and password
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pending) return;
    setErrorMsg("");
    setPending(true);

    try {
      if (isSignUp) {
        emailSignUpStartedRef.current = true;
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      emailSignUpStartedRef.current = false;
      setErrorMsg(localizedAuthErrorMessage(err, strings));
      setPending(false);
      return;
    }
    if (isSignUp) {
      try {
        await sendVerificationEmail(l);
        setVerificationEmailSentTo(email);
      } catch (err) {
        // The account works without it, and the create pages offer to
        // send it.
        console.error("Failed to send the verification email", err);
        navigate("/");
      }
    }
  };

  // Sign In with Google
  const handleGoogleSignIn = async () => {
    if (pending) return;
    setErrorMsg("");
    setPending(true);
    const provider = new GoogleAuthProvider();
    try {
      if (process.env.NODE_ENV === "development") {
        await signInWithPopup(auth, provider);
      } else {
        await signInWithRedirect(auth, provider);
        // After this, the browser redirects to the OAuth provider; we won't
        // reach the code below. The outcome is handled by the
        // getRedirectResult effect when the user lands back here.
      }
    } catch (err) {
      setErrorMsg(localizedAuthErrorMessage(err, strings));
      setPending(false);
    }
  };

  const loggedIn = useLoggedIn();
  useEffect(() => {
    if (loggedIn && !emailSignUpStartedRef.current) {
      navigate("/");
    }
  }, [loggedIn, navigate]);

  // Going Back from Google's sign-in page can restore this page from the
  // back/forward cache with its state intact; don't leave the buttons stuck
  // in the pending state.
  useEffect(() => {
    const resetPendingOnRestore = (e: PageTransitionEvent) => {
      if (e.persisted) setPending(false);
    };
    window.addEventListener("pageshow", resetPendingOnRestore);
    return () => window.removeEventListener("pageshow", resetPendingOnRestore);
  }, []);

  // Surface errors from the Google sign-in redirect flow. A null result just
  // means this page load isn't a return from the provider — only failures
  // need handling here; success is picked up by the loggedIn effect above.
  useEffect(() => {
    getRedirectResult(auth).catch((err) => {
      setErrorMsg(localizedAuthErrorMessage(err, strings));
    });
    // strings changes only with the locale; re-running on locale change is
    // harmless (getRedirectResult resolves null after the first consume).
  }, [strings]);

  if (verificationEmailSentTo) {
    return (
      <div className="flex flex-grow flex-col items-center justify-center py-6 sm:py-10">
        <div className="card w-full max-w-md rounded-3xl px-6 py-8 text-center sm:px-8">
          <Pup shape="circle" mood="happy" className="mx-auto h-16 w-16" />
          <h1 className="mt-4 font-literata text-[28px] font-bold text-main-text">
            {strings.login_verification_sent_title}
          </h1>
          <p className="mb-7 mt-1.5 break-words text-sm text-secondary-text">
            {strings.login_verification_sent_message.replace(
              "{email}",
              verificationEmailSentTo,
            )}
          </p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="btn-primary w-full px-4 py-3"
          >
            {strings.login_verification_sent_continue}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-grow flex-col items-center justify-center py-6 sm:py-10">
      <div className="card w-full max-w-md rounded-3xl px-6 py-8 sm:px-8">
        <Pup
          shape="circle"
          mood={isSignUp ? "happy" : "reading"}
          className="mx-auto h-16 w-16"
        />
        <h1 className="mt-4 text-center font-literata text-[28px] font-bold text-main-text">
          {isSignUp ? strings.login_title_signup : strings.login_title_signin}
        </h1>

        <p className="mb-7 mt-1.5 text-center text-sm text-secondary-text">
          {isSignUp
            ? strings.login_subtitle_signup
            : strings.login_subtitle_signin}
        </p>

        {errorMsg && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-error/30 bg-error/10 px-4 py-3"
          >
            <p className="text-sm text-error">{errorMsg}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-main-text"
            >
              {strings.login_email_label}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="input px-4 py-3"
              placeholder="your@email.com"
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="password"
              className="block text-sm font-medium text-main-text"
            >
              {strings.login_password_label}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="input px-4 py-3"
              placeholder="••••••••"
              autoComplete={isSignUp ? "new-password" : "current-password"}
              aria-describedby={isSignUp ? "password-hint" : undefined}
            />
            {/* State the rule up front instead of only after a rejection. */}
            {isSignUp && (
              <p id="password-hint" className="text-xs text-secondary-text">
                {strings.login_password_hint_signup}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={pending}
            className="btn-primary w-full px-4 py-3"
          >
            {isSignUp
              ? pending
                ? strings.login_submit_signup_pending
                : strings.login_submit_signup
              : pending
                ? strings.login_submit_signin_pending
                : strings.login_submit_signin}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-center">
          <hr className="flex-grow border-border" />
          <span className="px-4 text-xs font-medium uppercase tracking-wider text-secondary-text">
            {strings.login_or}
          </span>
          <hr className="flex-grow border-border" />
        </div>

        <button
          onClick={handleGoogleSignIn}
          disabled={pending}
          className="btn-secondary mt-5 w-full px-4 py-3 text-sm"
        >
          <svg
            className="h-5 w-5"
            viewBox="-3 0 262 262"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid"
          >
            <path
              d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
              fill="#4285F4"
            />
            <path
              d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
              fill="#34A853"
            />
            <path
              d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
              fill="#FBBC05"
            />
            <path
              d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
              fill="#EB4335"
            />
          </svg>
          {strings.login_google_button}
        </button>

        <p className="mt-8 text-center text-sm text-secondary-text">
          {isSignUp
            ? strings.login_toggle_to_signin_question
            : strings.login_toggle_to_signup_question}
          <Link
            to={isSignUp ? "/login" : "/signup"}
            className="font-semibold text-primary transition-colors hover:text-primary-hover"
          >
            {isSignUp
              ? strings.login_toggle_to_signin
              : strings.login_toggle_to_signup}
          </Link>
        </p>
      </div>
    </div>
  );
}

export function SignInPage({ l }: { l: string }) {
  return <LoginPage isSignUp={false} l={l} />;
}

export function SignUpPage({ l }: { l: string }) {
  return <LoginPage isSignUp={true} l={l} />;
}
